import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin,
  FileText,
  Download,
  Eye,
  X,
  CreditCard,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Check,
  XCircle
} from "lucide-react";
import { applicationsAPI } from "../../services/api";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://backend.aifia.in";

// Helper function to format date from YYYY-MM-DD to DD/M/YYYY
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export function ApplicationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({ type: "", path: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [application, setApplication] = useState(null);
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    fetchApplication();
  }, [id]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await applicationsAPI.getById(id);
      
      if (response.success) {
        setApplication(response.data);
        setPayment(response.data.payment);
      } else {
        setError(response.message || "Failed to fetch application");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch application details");
      console.error("Error fetching application:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (docType, filePath) => {
    if (!filePath) {
      alert("No document uploaded");
      return;
    }
    setCurrentDocument({ type: docType, path: filePath });
    setShowDocumentModal(true);
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setCurrentDocument({ type: "", path: "" });
  };

  const handleCloseTransactionModal = () => {
    setShowTransactionModal(false);
  };

  const handleDownloadDocument = async (docType, filePath) => {
    if (!filePath) {
      alert("No document uploaded");
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/${filePath}`);
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filePath.split('/').pop() || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback to opening in new tab
      window.open(`${API_BASE_URL}/${filePath}`, '_blank');
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed':
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
      case 'submitted':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'failed':
      case 'rejected':
      case 'insufficient':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getDocumentFileName = (path) => {
    if (!path) return "Not uploaded";
    return path.split('/').pop() || path.split('\\').pop();
  };

  const getIdTypeFromPath = (path) => {
    if (!path) return "ID Proof";
    const fileName = path.toLowerCase();
    if (fileName.includes('aadhar') || fileName.includes('aadhaar')) return "Aadhaar Card";
    if (fileName.includes('pan')) return "PAN Card";
    if (fileName.includes('voter')) return "Voter ID";
    if (fileName.includes('passport')) return "Passport";
    if (fileName.includes('dl') || fileName.includes('driving')) return "Driving License";
    return "ID Proof";
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <Link to="/applications" className="inline-flex items-center gap-2 text-[#C46A1B] hover:text-[#9E4E10] transition">
          <ArrowLeft className="w-4 h-4" />
          Back to Applications
        </Link>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-[#C46A1B] mx-auto mb-2" />
            <p className="text-gray-500">Loading application details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <Link to="/applications" className="inline-flex items-center gap-2 text-[#C46A1B] hover:text-[#9E4E10] transition">
          <ArrowLeft className="w-4 h-4" />
          Back to Applications
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex gap-2">
            <button
              onClick={fetchApplication}
              className="px-4 py-2 bg-[#C46A1B] text-white rounded hover:bg-[#9E4E10]"
            >
              Retry
            </button>
            <button
              onClick={() => navigate("/applications")}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              Back to Applications
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No application found
  if (!application) {
    return (
      <div className="space-y-6">
        <Link to="/applications" className="inline-flex items-center gap-2 text-[#C46A1B] hover:text-[#9E4E10] transition">
          <ArrowLeft className="w-4 h-4" />
          Back to Applications
        </Link>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <p className="text-yellow-700">Application not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link to="/applications" className="inline-flex items-center gap-2 text-[#C46A1B] hover:text-[#9E4E10] transition">
        <ArrowLeft className="w-4 h-4" />
        Back to Applications
      </Link>

      {/* Header with Status */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Application #{application.application_number}</h1>
          <p className="text-gray-600">View application details</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Applicant Details (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[#111111] mb-4">Personal Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-medium flex items-center gap-2 mt-1">
                  <User className="w-4 h-4 text-[#C46A1B]" />
                  {application.full_name || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Card Type</p>
                <p className="font-medium">{application.card_type || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4 text-[#C46A1B]" />
                  {application.email || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium flex items-center gap-2 mt-1">
                  <Phone className="w-4 h-4 text-[#C46A1B]" />
                  {application.phone || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Age</p>
                <p className="font-medium">{application.age ? `${application.age} years` : "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Gender</p>
                <p className="font-medium">{application.gender || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Position</p>
                <p className="font-medium">{application.position || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Applied Date</p>
                <p className="font-medium flex items-center gap-2 mt-1">
                  <Calendar className="w-4 h-4 text-[#C46A1B]" />
                  {formatDate(application.applied_date)}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium flex items-center gap-2 mt-1">
                  <MapPin className="w-4 h-4 text-[#C46A1B]" />
                  {application.address || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Documents with View Option */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[#111111] mb-4">Uploaded Documents</h2>
            <div className="space-y-3">
              {/* Photo Document */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#C46A1B]" />
                  <div>
                    <p className="font-medium">Photo</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {application.photo_status === 'submitted' && (
                    <span className="text-xs text-blue-600 font-medium">Submitted</span>
                  )}
                  {application.photo_status === 'failed' && (
                    <span className="text-xs text-red-600 font-medium">Failed</span>
                  )}
                  {application.photo_status === 'pending' && (
                    <span className="text-xs text-yellow-600 font-medium">Pending</span>
                  )}
                  <button 
                    onClick={() => handleViewDocument('Photo', application.photo_path)}
                    className="flex items-center gap-1 text-[#C46A1B] hover:text-[#9E4E10] text-sm px-2 py-1 rounded-md hover:bg-orange-50 transition"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                  <button 
                    onClick={() => handleDownloadDocument('Photo', application.photo_path)}
                    className="flex items-center gap-1 text-[#C46A1B] hover:text-[#9E4E10] text-sm px-2 py-1 rounded-md hover:bg-orange-50 transition"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>

              {/* ID Proof Document */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#C46A1B]" />
                  <div>
                    <p className="font-medium">ID Proof</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {application.id_proof_status === 'submitted' && (
                    <span className="text-xs text-blue-600 font-medium">Submitted</span>
                  )}
                  {application.id_proof_status === 'failed' && (
                    <span className="text-xs text-red-600 font-medium">Failed</span>
                  )}
                  {application.id_proof_status === 'pending' && (
                    <span className="text-xs text-yellow-600 font-medium">Pending</span>
                  )}
                  <button 
                    onClick={() => handleViewDocument('ID Proof', application.id_proof_path)}
                    className="flex items-center gap-1 text-[#C46A1B] hover:text-[#9E4E10] text-sm px-2 py-1 rounded-md hover:bg-orange-50 transition"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                  <button 
                    onClick={() => handleDownloadDocument('ID Proof', application.id_proof_path)}
                    className="flex items-center gap-1 text-[#C46A1B] hover:text-[#9E4E10] text-sm px-2 py-1 rounded-md hover:bg-orange-50 transition"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Right Column - Payment Details (1/3 width) */}
        <div className="lg:col-span-1">
          {/* Payment Details */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-[#111111] mb-4">Payment Details</h2>
            {payment ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-semibold text-lg">₹{payment.amount || "0"}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Payment Status</span>
                  <span className={`text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1 ${
                    payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                    payment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {getStatusIcon(payment.status)}
                    {payment.status ? payment.status.charAt(0).toUpperCase() + payment.status.slice(1) : 'N/A'}
                  </span>
                </div>
                
                <div className="pt-2">
                  <p className="text-sm text-gray-500 mb-1">Transaction ID</p>
                  <p className="font-mono text-sm bg-gray-50 p-2 rounded">{payment.payment_number || "N/A"}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-1">Payment Date</p>
                  <p className="font-medium">{formatDate(payment.payment_date)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-1">Payment Method</p>
                  <p className="font-medium">{payment.payment_method || "N/A"}</p>
                </div>

                {payment.utr_number && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">UTR Number</p>
                    <p className="font-mono text-sm bg-gray-50 p-2 rounded">{payment.utr_number}</p>
                  </div>
                )}
                
                <button 
                  onClick={() => setShowTransactionModal(true)}
                  className="w-full mt-4 flex items-center justify-center gap-2 bg-[#C46A1B] text-white px-4 py-3 rounded-lg hover:bg-[#9E4E10] transition"
                >
                  <CreditCard className="w-4 h-4" />
                  View Transaction
                </button>
              </div>
            ) : (
              <div className="text-center py-6">
                <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No payment found</p>
                <p className="text-sm text-gray-400 mt-1">Payment pending or not initiated</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document View Modal - Full screen overlay with inline styles */}
      {showDocumentModal && currentDocument.path && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 0,
            margin: 0
          }}
          onClick={handleCloseDocumentModal}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C46A1B]/10 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#C46A1B]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#111111]">
                    {currentDocument.type} Document
                  </h3>
                </div>
              </div>
              <button
                onClick={handleCloseDocumentModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body - Document Preview */}
            <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
              <div className="bg-gray-100 rounded-xl p-0 flex flex-col items-center justify-center min-h-[0px] border-2 border-dashed border-gray-300">
                {currentDocument.path?.toLowerCase().includes(".pdf") ? (
                  <>
                    <FileText className="w-20 h-20 text-gray-400 mb-4" />
                    <p className="text-gray-500 text-center mb-2">
                      PDF Document
                    </p>
                    <a
                      href={`${API_BASE_URL}/${currentDocument.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#C46A1B] hover:underline"
                    >
                      Open PDF in new tab
                    </a>
                  </>
                ) : (
                  <>
                    <img
                      src={`${API_BASE_URL}/${currentDocument.path}`}
                      alt={`${currentDocument.type} Preview`}
                      className="max-w-full max-h-[400px] object-contain rounded-lg shadow-md"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/400x300?text=No+Preview";
                      }}
                    />
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleCloseDocumentModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleDownloadDocument(
                      currentDocument.type,
                      currentDocument.path,
                    );
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#C46A1B] text-white rounded-lg hover:bg-[#9E4E10] transition"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Details Modal - Full screen overlay with inline styles */}
      {showTransactionModal && payment && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 0,
            margin: 0
          }}
          onClick={handleCloseTransactionModal}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#C46A1B]/10 rounded-full flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-[#C46A1B]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#111111]">Transaction Details</h3>
                  <p className="text-sm text-gray-500">Transaction ID: {payment.payment_number}</p>
                </div>
              </div>
              <button
                onClick={handleCloseTransactionModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-xl">
                <span className="text-gray-600">Transaction Status</span>
                <span className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                  payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                  payment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {getStatusIcon(payment.status)}
                  {payment.status ? payment.status.charAt(0).toUpperCase() + payment.status.slice(1) : 'N/A'}
                </span>
              </div>

              {/* Transaction Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Amount</p>
                  <p className="text-xl font-bold text-[#111111]">₹{payment.amount}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                  <p className="font-medium">{payment.payment_method || "N/A"}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Date & Time</p>
                  <p className="font-medium">{formatDate(payment.payment_date)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">UTR Number</p>
                  <p className="font-mono text-sm font-medium">{payment.utr_number || "N/A"}</p>
                </div>
              </div>

              {/* Transaction ID */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Transaction ID</p>
                <p className="font-mono font-medium">{payment.payment_number || "N/A"}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={handleCloseTransactionModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
